<script setup lang="ts">
import { useFrameStore } from '../stores/frameStore'
import { useUiStore } from '../stores/uiStore'
import { apiClient } from '../services/apiClient'
import AvatarButton from './AvatarButton.vue'

interface User {
  id: number
  email: string
  username: string
  nickname: string
  role: 'sysadmin' | 'group_admin' | 'user'
}

interface Props {
  isLoggedIn?: boolean
  user?: User | null
  snapshotCount?: number
  bookmarkCount?: number
  noteCount?: number
}

const frameStore = useFrameStore()
const uiStore = useUiStore()

const props = withDefaults(defineProps<Props>(), {
  isLoggedIn: false,
  user: null,
  snapshotCount: 0,
  bookmarkCount: 0,
  noteCount: 0,
})

const emit = defineEmits<{
  'toggle-snapshot-panel': []
  'save-snapshot': []
  'show-auth-dialog': []
  'logout-confirm': []
}>()

const handleAddFrame = async () => {
  const COLORS = ['blue', 'green', 'orange', 'purple', 'red', 'cyan']
  const SPACING = 40
  const MIN_WIDTH = 300
  const MIN_HEIGHT = 200
  const DESKTOP_WIDTH = 4000
  const DESKTOP_HEIGHT = 3000

  try {
    const width = Math.max(MIN_WIDTH, Math.floor(window.innerWidth / 3))
    const height = Math.max(MIN_HEIGHT, Math.floor((window.innerHeight - uiStore.toolbarHeight) / 2))

    const nextId = Date.now()
    const nextFrameIndex = frameStore.frames.length

    // 計算新框的位置：在現有框的右邊或下一行
    let newX = 60
    let newY = 60

    if (frameStore.frames.length > 0) {
      // 查找頂部框（y 接近 60）
      const topFrames = frameStore.frames.filter(f => Math.abs((f.y as any) - 60) <= 20)
      if (topFrames.length > 0) {
        // 放在最右邊的框的右邊
        const rightmost = topFrames.reduce((max, f) => {
          const maxX = (max.x as any) + (max.w as any)
          const fX = (f.x as any) + (f.w as any)
          return fX > maxX ? f : max
        })
        newX = ((rightmost.x as any) + (rightmost.w as any) + SPACING)
      } else {
        // 如果沒有頂部框，放在第一行最右邊的後面
        const rightmost = frameStore.frames.reduce((max, f) =>
          ((f.x as any) + (f.w as any)) > ((max.x as any) + (max.w as any)) ? f : max
        )
        newX = ((rightmost.x as any) + (rightmost.w as any) + SPACING)
      }
    }

    // 檢查是否超出桌面邊界，如果超出就換行
    if (newX + width > DESKTOP_WIDTH) {
      newX = 60
      newY = 60 + height + SPACING
    }

    const frameData = {
      frameId: nextId,
      x: newX,
      y: newY,
      w: width,
      h: height,
      title: `功能框 ${nextFrameIndex + 1}`,
      type: 'bible-reading' as const,
      color: COLORS[nextFrameIndex % COLORS.length],
    }

    // 只在 frameStore 中添加框，不保存到數據庫
    // frames 只在用戶按下"記錄版面"時才保存為快照
    frameStore.frames.push({
      id: -nextId,
      frameId: nextId,
      x: newX,
      y: newY,
      w: width,
      h: height,
      title: frameData.title,
      type: frameData.type,
      color: frameData.color,
    })
  } catch (error) {
    console.error('Failed to add frame:', error)
    alert(`新增功能框失敗: ${error instanceof Error ? error.message : '未知錯誤'}`)
  }
}

const handleToggleSnapshotPanel = () => {
  emit('toggle-snapshot-panel')
}
</script>

<template>
  <div class="toolbar border-b border-gray-300 bg-white flex items-center px-4 gap-2 shadow-sm" :style="{ height: uiStore.toolbarHeight + 'px' }">
    <h1 class="font-semibold text-gray-700" :style="{ fontSize: uiStore.fontSize + 'px' }">聖經研讀系統 v0.4.0</h1>
    <div class="flex-1" />
    <div class="flex items-center gap-2">
      <button
        class="toolbar-btn"
        @click="handleAddFrame"
        title="新增功能框"
      >
        ➕ 新增功能框
      </button>
      <button
        class="toolbar-btn"
        @click="emit('save-snapshot')"
        title="儲存當前版面配置"
      >
        💾 儲存版面
      </button>
      <button
        class="toolbar-btn"
        @click="handleToggleSnapshotPanel"
        title="打開版面記錄清單"
      >
        📋 版面記錄
      </button>
      <AvatarButton
        :is-logged-in="props.isLoggedIn"
        :user="props.user"
        :snapshot-count="props.snapshotCount"
        :bookmark-count="props.bookmarkCount"
        :note-count="props.noteCount"
        @show-auth-dialog="$emit('show-auth-dialog')"
        @logout-confirm="$emit('logout-confirm')"
      />
    </div>
  </div>
</template>

<style scoped>
.toolbar {
  background-color: #ffffff;
  border-bottom: 1px solid #d1d5db;
  z-index: 100;
}

.toolbar-btn {
  background-color: transparent;
  border: 1px solid #999;
  padding: 4px 8px;
  height: var(--btn-height);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-base);
  transition: all 0.2s;
  white-space: nowrap;
  color: #333;
  font-weight: 500;
}

.toolbar-btn:hover {
  background-color: #d0d0d0;
  border-color: #666;
}

.toolbar-btn.danger {
  border-color: #ff6b6b;
  color: #ff6b6b;
}

.toolbar-btn.danger:hover {
  background-color: #fff5f5;
  border-color: #ff4444;
}

</style>
